import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ChevronLeft, Loader2, Clock, Pencil, LayoutTemplate as LayoutIcon,
  Video, FileType, FileText, Image as ImageIcon, Trash2,
  Upload, ChevronDown, MoreHorizontal, Copy,
  Play, Pause, Plus, GripVertical, X, Link2, Maximize2, Minimize2,
  CalendarClock, Volume2, VolumeX, Save,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Badge } from '@meno/ui/components/ui/badge'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@meno/ui/components/ui/dialog'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@meno/ui/components/ui/dropdown-menu'
import {
  getSignageLayout, updateSignageLayout, deleteSignageLayout, publishSignageLayout,
  BACKEND_URL, getMedia,
} from '@meno/ui/lib/api'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { useIsMobile } from '@meno/ui/hooks/use-mobile'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { formatDate } from '@meno/ui/lib/utils'
import PlaylistContentPicker, { type PickedItem } from '@/components/PlaylistContentPicker'
import { LayoutPickerGrid } from '@/components/LayoutDialog'
import { PublishDialog } from '@/components/PublishDialog'
import { withCompanions } from '@/lib/companions'
import { RowScheduleDialog } from '@/components/RowScheduleDialog'
import {
  isVisibleAt,
  DEFAULT_ROW_SCHEDULE,
  type RowSchedule,
} from '@/lib/rowSchedule'
import {
  getTemplate, buildEmptyZones, gridTracks, zoneStyle,
  type LayoutTemplate, type TemplateDef,
} from '@/lib/layoutTemplates'
import { loadLinks as loadInteractiveLinks, type LinkItem } from '@/lib/interactiveLinks'
import PdfThumbnail from '@/components/PdfThumbnail'
import DurationInput from '@/components/DurationInput'

type MediaFile = { id: number; original_name: string; file_type: string; url: string; duration?: number }
// Slim shape we render in rows — only the fields ContentRowView and the
// preview tile actually read.
type RowLink = { id: number; name: string; interactive_link: string }
type ScreenRef = { id: number; name: string }

// Schedule window as it comes off the wire (mirror of the backend's
// ScheduleWindow schema — both bounds may be null). The frontend converts
// these to RowSchedule at the row level for the dialog.
type WireScheduleWindow = { start: string | null; end: string | null }

type LayoutZone = {
  id: string
  name: string
  media_ids: number[]
  link_ids: number[]
  media_durations: Record<number, number>
  link_durations: Record<number, number>
  media_order: Record<number, number>
  link_order:  Record<number, number>
  // Mirror of Playlist's per-row fields — kept inside the zone JSON because
  // a layout's content lives per-zone, not on a flat M2M like playlists.
  media_muted: Record<number, boolean>
  media_schedule: Record<number, WireScheduleWindow>
  link_schedule:  Record<number, WireScheduleWindow>
}

type LayoutData = {
  id: number
  name: string
  description?: string
  template: LayoutTemplate
  zones: LayoutZone[]
  column_weights?: number[] | null
  row_weights?: number[] | null
  screens: ScreenRef[]
  created_at: string
  // Publish companions (audio source / stop screen / transition) — chosen in
  // the create dialog and co-published with the layout by withCompanions.
  companion_audio_id?: number | null
  companion_audio_playlist_id?: number | null
  companion_stop_screen_id?: number | null
  companion_transition?: string | null
}

// One row in a zone's content list, mirroring PlaylistDetailsPage but
// referencing items by ID-and-lookup so we can build it cheaply on the fly.
type ContentRow =
  | { key: string; kind: 'media'; pos: number; item: MediaFile; duration: number; isVideo: boolean; isPdf: boolean; isImage: boolean; muted: boolean }
  | { key: string; kind: 'link';  pos: number; item: RowLink;   duration: number }

const THUMB_CACHE_PREFIX = 'meno_vthumb_'

function fmtDur(sec: number) {
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function defaultDurationFor(row: ContentRow): number {
  if (row.kind === 'link') return row.duration > 0 ? row.duration : 10
  if (row.isVideo) return row.duration || row.item.duration || 10
  return row.duration > 0 ? row.duration : 10
}

// Hydrate a zone payload from the wire — missing per-row maps default to
// empty so the rest of the page can read them without `?? {}` guards.
function hydrateZone(raw: Partial<LayoutZone> & { id: string }): LayoutZone {
  return {
    id: raw.id,
    name: raw.name ?? 'Zone',
    media_ids: raw.media_ids ?? [],
    link_ids:  raw.link_ids  ?? [],
    media_durations: raw.media_durations ?? {},
    link_durations:  raw.link_durations  ?? {},
    media_order: raw.media_order ?? {},
    link_order:  raw.link_order  ?? {},
    media_muted: raw.media_muted ?? {},
    media_schedule: raw.media_schedule ?? {},
    link_schedule:  raw.link_schedule  ?? {},
  }
}

// Signature of everything a save persists — used to detect buffered, unsaved
// edits (name, template, zone content + orders/durations/mute/schedules, and
// the zone weights).
function layoutSig(l: LayoutData): string {
  return JSON.stringify({
    name: l.name,
    template: l.template,
    cw: l.column_weights ?? null,
    rw: l.row_weights ?? null,
    zones: l.zones.map(z => ({
      id: z.id,
      m: z.media_ids, l: z.link_ids,
      mo: z.media_order, lo: z.link_order,
      md: z.media_durations, ld: z.link_durations,
      mm: z.media_muted, ms: z.media_schedule, ls: z.link_schedule,
    })),
  })
}

// Derive a layout-level header thumbnail the same way Playlist does — first
// renderable image (or cached video frame) walking all zones in order.
function layoutThumbUrl(layout: LayoutData | null, allMedia: MediaFile[]): string | null {
  if (!layout) return null
  const mediaById = new Map<number, MediaFile>(allMedia.map(m => [m.id, m]))
  for (const z of layout.zones) {
    for (const mid of z.media_ids) {
      const m = mediaById.get(mid)
      if (!m) continue
      if (m.file_type.startsWith('image')) {
        return m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`
      }
      if (m.file_type.startsWith('video')) {
        try {
          const cached = localStorage.getItem(`${THUMB_CACHE_PREFIX}${m.id}`)
          if (cached) return cached
        } catch { /* localStorage unavailable */ }
      }
    }
  }
  return null
}

export default function LayoutDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  // Always ChevronLeft — a global CSS rule mirrors it under dir="rtl"; see
  // WizardNavButton.tsx for why swapping it here too double-flips it back.
  const ChevronIcon = ChevronLeft
  // Row reordering below uses native HTML5 drag-and-drop, which doesn't
  // respond to touch — surface a hint rather than let taps silently do
  // nothing (zone layout/resize itself uses pointer events and works fine).
  const isMobile = useIsMobile()

  const [layout, setLayout] = useState<LayoutData | null>(null)
  const [saving, setSaving] = useState(false)
  // Edits buffer into `layout` locally and are only persisted on Save / Publish;
  // `dirty` compares the live layout to the last-saved signature.
  const [savedSig, setSavedSig] = useState('')
  const dirty = layout != null && layoutSig(layout) !== savedSig
  const { guardOpen, guardNavigate, confirmLeave, cancelLeave } = useUnsavedChangesGuard(dirty)
  const [allMedia, setAllMedia] = useState<MediaFile[]>([])
  const [allLinks, setAllLinks] = useState<LinkItem[]>([])
  const [loading, setLoading] = useState(true)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [changeLayoutOpen, setChangeLayoutOpen] = useState(false)
  const [thumbCache, setThumbCache] = useState<Record<number, string>>({})

  const [activeZoneId, setActiveZoneId] = useState<string>('z1')
  const [zoneExpanded, setZoneExpanded] = useState(false)

  // Publish / Schedule share one dialog (PublishDialog); the mode picks
  // whether the start/end window inputs are shown. null = closed.
  const [publishMode, setPublishMode] = useState<'publish' | 'schedule' | null>(null)

  const [dragKey, setDragKey]         = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const [titleDraft, setTitleDraft] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  const [previewing, setPreviewing] = useState(false)

  // Per-row schedule dialog target — `${zoneId}:${rowKey}` so we can route
  // the save back to the right zone's schedule map.
  const [scheduleTarget, setScheduleTarget] = useState<string | null>(null)
  const [now, setNow] = useState<Date>(() => new Date())
  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(tick)
  }, [])

  // ── Initial load ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return
    const load = async () => {
      try {
        const { data: lay } = await getSignageLayout(Number(id))
        const hydrated = { ...lay, zones: (lay.zones ?? []).map(hydrateZone) }
        setLayout(hydrated)
        setSavedSig(layoutSig(hydrated))
        if (lay.zones?.length) setActiveZoneId(lay.zones[0].id)
      } catch {
        toast.error(t('layout.loadFailed', 'Failed to load layout'))
        navigate('/content-hub')
        return
      } finally {
        setLoading(false)
      }

      getMedia(false, 'signage')
        .then(({ data }) => {
          setAllMedia(data)
          const cache: Record<number, string> = {}
          ;(data as MediaFile[]).forEach(m => {
            if (m.file_type.startsWith('video')) {
              const stored = localStorage.getItem(`${THUMB_CACHE_PREFIX}${m.id}`)
              if (stored) cache[m.id] = stored
            }
          })
          setThumbCache(cache)
        })
        .catch(() => {})
      loadInteractiveLinks().then(setAllLinks).catch(() => {})
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const activeZone: LayoutZone | undefined = useMemo(
    () => layout?.zones.find(z => z.id === activeZoneId),
    [layout, activeZoneId],
  )

  // Resolve a per-row schedule from the zone's wire-format map. Missing /
  // empty windows mean "always on".
  const getSchedule = useCallback((zone: LayoutZone, rowKey: string): RowSchedule => {
    const rid = Number(rowKey.slice(2))
    const map = rowKey.startsWith('m-') ? zone.media_schedule : zone.link_schedule
    const w = map?.[rid]
    if (!w || (!w.start && !w.end)) return DEFAULT_ROW_SCHEDULE
    return { alwaysOn: false, startAt: w.start ?? undefined, endAt: w.end ?? undefined }
  }, [])

  // Build the ordered ContentRow list for a zone — mirrors PlaylistDetailsPage.
  const rowsForZone = useCallback((zone: LayoutZone | undefined): ContentRow[] => {
    if (!zone) return []
    const out: ContentRow[] = []
    zone.media_ids.forEach(mid => {
      const m = allMedia.find(x => x.id === mid)
      if (!m) return
      const isImage = m.file_type.startsWith('image')
      const isVideo = m.file_type.startsWith('video')
      const isPdf   = m.file_type === 'application/pdf' || /\.pdf$/i.test(m.original_name)
      const explicit = zone.media_durations[mid]
      const duration = explicit ?? (isVideo ? (m.duration ?? 0) : 10)
      out.push({
        key: `m-${mid}`, kind: 'media',
        pos: zone.media_order[mid] ?? 1e6,
        item: m, duration, isImage, isVideo, isPdf,
        muted: !!zone.media_muted?.[mid],
      })
    })
    zone.link_ids.forEach(lid => {
      const l = allLinks.find(x => x.id === lid)
      if (!l) return
      out.push({
        key: `l-${lid}`, kind: 'link',
        pos: zone.link_order[lid] ?? 1e6,
        item: { id: l.id, name: l.name, interactive_link: l.interactive_link },
        duration: zone.link_durations[lid] ?? 10,
      })
    })
    return out.sort((a, b) => a.pos - b.pos)
  }, [allMedia, allLinks])

  const activeRows = useMemo(() => rowsForZone(activeZone), [activeZone, rowsForZone])

  // ── Local edit buffer ──────────────────────────────────────────────────
  // Every mutation writes the full zones array (and optional template/weights)
  // into local state only. Nothing is persisted until Save / Publish, so the
  // layout "doesn't change" until the operator commits it.
  const persistZones = async (
    nextZones: LayoutZone[],
    opts?: { template?: LayoutTemplate; column_weights?: number[] | null; row_weights?: number[] | null },
  ) => {
    setLayout(prev => prev ? {
      ...prev,
      zones: nextZones,
      ...(opts?.template ? { template: opts.template } : {}),
      ...(opts && 'column_weights' in opts ? { column_weights: opts.column_weights } : {}),
      ...(opts && 'row_weights' in opts ? { row_weights: opts.row_weights } : {}),
    } : prev)
  }

  const persistWeights = useCallback(
    async (column_weights: number[] | null, row_weights: number[] | null) => {
      setLayout(prev => prev ? { ...prev, column_weights, row_weights } : prev)
    },
    [],
  )

  // Flush buffered edits to the backend in one PATCH. Save / Publish are the
  // only writers now.
  const saveChanges = async (): Promise<LayoutData | null> => {
    if (!layout) return null
    setSaving(true)
    try {
      const { data } = await updateSignageLayout(layout.id, {
        name: layout.name,
        template: layout.template,
        zones: layout.zones,
        column_weights: layout.column_weights ?? null,
        row_weights: layout.row_weights ?? null,
      })
      const hydrated = { ...data, zones: (data.zones ?? []).map(hydrateZone) }
      setLayout(hydrated)
      setSavedSig(layoutSig(hydrated))
      return data
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
      return null
    } finally {
      setSaving(false)
    }
  }

  const [draftColWeights, setDraftColWeights] = useState<number[] | null>(null)
  const [draftRowWeights, setDraftRowWeights] = useState<number[] | null>(null)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraftColWeights(layout?.column_weights ?? null)
    setDraftRowWeights(layout?.row_weights ?? null)
  }, [layout?.column_weights, layout?.row_weights])

  const resetWeights = async () => {
    setDraftColWeights(null)
    setDraftRowWeights(null)
    await persistWeights(null, null)
    toast.success(t('layout.resetSizes', 'Zone sizes reset'))
  }

  const writeActiveZone = (mutator: (z: LayoutZone) => LayoutZone) => {
    if (!layout || !activeZone) return Promise.resolve()
    const next = layout.zones.map(z => z.id === activeZone.id ? mutator({ ...z }) : z)
    return persistZones(next)
  }

  // Append picked items to the active zone, skipping items already there.
  const handleAppend = async (picked: PickedItem[]) => {
    setPickerOpen(false)
    if (!activeZone || picked.length === 0) return
    const existingMedia = new Set(activeZone.media_ids)
    const existingLinks = new Set(activeZone.link_ids)
    const newMediaIds: number[] = []
    const newLinkIds: number[]  = []
    const newDurations: Record<number, number> = { ...activeZone.media_durations }
    const newLinkDurations: Record<number, number> = { ...activeZone.link_durations }
    let nextPos = activeZone.media_ids.length + activeZone.link_ids.length
    const newMediaOrder: Record<number, number> = { ...activeZone.media_order }
    const newLinkOrder:  Record<number, number> = { ...activeZone.link_order  }
    for (const p of picked) {
      if (p.kind === 'media' && !existingMedia.has(p.id)) {
        newMediaIds.push(p.id)
        const isVideo = p.file_type.startsWith('video')
        newDurations[p.id] = isVideo ? (p.duration ?? 10) : 10
        newMediaOrder[p.id] = nextPos++
      } else if (p.kind === 'link' && !existingLinks.has(p.id)) {
        newLinkIds.push(p.id)
        newLinkDurations[p.id] = 10
        newLinkOrder[p.id] = nextPos++
      }
    }
    if (newMediaIds.length === 0 && newLinkIds.length === 0) return
    await writeActiveZone(z => ({
      ...z,
      media_ids: [...z.media_ids, ...newMediaIds],
      link_ids:  [...z.link_ids,  ...newLinkIds],
      media_durations: newDurations,
      link_durations:  newLinkDurations,
      media_order: newMediaOrder,
      link_order:  newLinkOrder,
    }))
    if (picked.some(p => p.kind === 'media')) {
      const { data } = await getMedia(false, 'signage')
      setAllMedia(data)
    }
    toast.success(t('layout.contentAdded', 'Added to zone'))
  }

  const handleRemoveRow = async (rowKey: string) => {
    if (!activeZone) return
    const isMedia = rowKey.startsWith('m-')
    const rid = Number(rowKey.slice(2))
    await writeActiveZone(z => {
      if (isMedia) {
        const durs  = { ...z.media_durations };  delete durs[rid]
        const ords  = { ...z.media_order };      delete ords[rid]
        const mutes = { ...z.media_muted };      delete mutes[rid]
        const sched = { ...z.media_schedule };   delete sched[rid]
        return {
          ...z,
          media_ids: z.media_ids.filter(x => x !== rid),
          media_durations: durs, media_order: ords,
          media_muted: mutes, media_schedule: sched,
        }
      } else {
        const durs  = { ...z.link_durations };  delete durs[rid]
        const ords  = { ...z.link_order };      delete ords[rid]
        const sched = { ...z.link_schedule };   delete sched[rid]
        return {
          ...z,
          link_ids: z.link_ids.filter(x => x !== rid),
          link_durations: durs, link_order: ords, link_schedule: sched,
        }
      }
    })
  }

  const handleDrop = async (fromKey: string, toKey: string, dropBelow: boolean) => {
    if (!activeZone || fromKey === toKey) return
    const rows = activeRows
    const fromIdx = rows.findIndex(r => r.key === fromKey)
    let   toIdx   = rows.findIndex(r => r.key === toKey)
    if (fromIdx < 0 || toIdx < 0) return
    const moved = rows[fromIdx]
    const without = rows.filter((_, i) => i !== fromIdx)
    if (fromIdx < toIdx) toIdx -= 1
    const insertAt = dropBelow ? toIdx + 1 : toIdx
    without.splice(insertAt, 0, moved)
    const mediaOrder: Record<number, number> = {}
    const linkOrder:  Record<number, number> = {}
    without.forEach((row, i) => {
      if (row.kind === 'media') mediaOrder[row.item.id] = i
      else                       linkOrder[row.item.id]  = i
    })
    await writeActiveZone(z => ({ ...z, media_order: mediaOrder, link_order: linkOrder }))
  }

  const handleDurationChange = async (rowKey: string, seconds: number) => {
    const isMedia = rowKey.startsWith('m-')
    const rid = Number(rowKey.slice(2))
    const val = Math.max(1, seconds)
    await writeActiveZone(z => isMedia
      ? { ...z, media_durations: { ...z.media_durations, [rid]: val } }
      : { ...z, link_durations:  { ...z.link_durations,  [rid]: val } },
    )
  }

  // Per-row mute (videos only). Mirror of Playlist's handleToggleMute — flip
  // the zone's media_muted map and persist the whole zone payload.
  const handleToggleMute = async (rid: number) => {
    if (!activeZone) return
    const current = !!activeZone.media_muted?.[rid]
    await writeActiveZone(z => ({
      ...z,
      media_muted: { ...(z.media_muted || {}), [rid]: !current },
    }))
  }

  // Per-row schedule. Same wire shape as Playlist: `{start:null,end:null}`
  // clears it back to always-on. We rebuild the zone's whole schedule map
  // (cheap — usually <10 entries) so the PATCH payload always reflects the
  // final desired state.
  const updateSchedule = async (zoneId: string, rowKey: string, next: RowSchedule) => {
    if (!layout) return
    const rid = Number(rowKey.slice(2))
    const win: WireScheduleWindow = next.alwaysOn
      ? { start: null, end: null }
      : { start: next.startAt ?? null, end: next.endAt ?? null }
    const nextZones = layout.zones.map(z => {
      if (z.id !== zoneId) return z
      if (rowKey.startsWith('m-')) {
        const map = { ...(z.media_schedule || {}) }
        if (!win.start && !win.end) delete map[rid]
        else map[rid] = win
        return { ...z, media_schedule: map }
      } else {
        const map = { ...(z.link_schedule || {}) }
        if (!win.start && !win.end) delete map[rid]
        else map[rid] = win
        return { ...z, link_schedule: map }
      }
    })
    await persistZones(nextZones)
  }

  // ── Template / Change Zone ────────────────────────────────────────────
  const handleChangeTemplate = async (nextKey: LayoutTemplate) => {
    if (!layout) return
    if (nextKey === layout.template) {
      setChangeLayoutOpen(false)
      return
    }
    const def = getTemplate(nextKey)
    const target = def.zones.length
    const empty = buildEmptyZones(nextKey).map(z => hydrateZone(z as LayoutZone))
    const next: LayoutZone[] = []
    for (let i = 0; i < target; i++) {
      next.push(layout.zones[i] ?? empty[i])
    }
    setChangeLayoutOpen(false)
    setDraftColWeights(null)
    setDraftRowWeights(null)
    await persistZones(next, { template: nextKey, column_weights: null, row_weights: null })
    if (!next.find(z => z.id === activeZoneId)) {
      setActiveZoneId(next[0].id)
    }
  }

  // ── Title rename + delete ──────────────────────────────────────────────
  const startTitleEdit = () => {
    if (!layout) return
    setTitleDraft(layout.name)
    setEditingTitle(true)
    setTimeout(() => titleInputRef.current?.focus(), 0)
  }
  const commitTitleEdit = () => {
    if (!layout) return
    const next = titleDraft.trim()
    setEditingTitle(false)
    if (!next || next === layout.name) return
    // Buffered — persisted on Save / Publish.
    setLayout({ ...layout, name: next })
  }
  const handleDelete = async () => {
    if (!layout) return
    try {
      await deleteSignageLayout(layout.id)
      toast.success(t('common.deleted', 'Deleted'))
      navigate('/management/content-hub')
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    }
    setDeleteOpen(false)
  }

  // ── Preview ────────────────────────────────────────────────────────────
  // Each preview tile self-manages its cycle (so zones with different
  // lengths run independently).

  // Build the per-zone row map respecting per-row schedules — hidden items
  // are dropped from the preview so it matches what a screen would render.
  const zoneRowsMap = useMemo(() => {
    const map: Record<string, ContentRow[]> = {}
    layout?.zones.forEach(z => {
      const rows = rowsForZone(z)
      map[z.id] = rows.filter(r => isVisibleAt(getSchedule(z, r.key), now))
    })
    return map
  }, [layout, rowsForZone, getSchedule, now])

  const hasAnyContent = Object.values(zoneRowsMap).some(r => r.length > 0)
  const togglePreview = () => {
    if (!hasAnyContent) return
    setPreviewing(v => !v)
  }

  // Header thumbnail — first renderable image / cached video frame across
  // all zones. Falls back to the Layout icon (Clapperboard-style) when
  // nothing renders yet.
  const headerThumb = useMemo(() => layoutThumbUrl(layout, allMedia), [layout, allMedia])

  // ── Info bar ────────────────────────────────────────────────────────────
  const infoBar = useMemo(() => {
    if (!layout) return null
    return (
      <div className="flex items-center justify-between gap-3 w-full ps-2 pe-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => guardNavigate(() => navigate(-1))}
            aria-label={t('common.back', 'Back')}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <ChevronIcon className="w-4 h-4" />
          </button>
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0 overflow-hidden">
            {headerThumb
              ? <img src={headerThumb} alt={layout.name} className="w-full h-full object-cover" />
              : <LayoutIcon className="w-5 h-5 text-amber-600" />}
          </div>
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 min-w-0">
              {editingTitle ? (
                <input
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onBlur={commitTitleEdit}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitTitleEdit()
                    if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(layout.name) }
                  }}
                  maxLength={150}
                  className="text-[15px] font-semibold leading-tight bg-transparent border-b border-primary outline-none min-w-0 w-48"
                />
              ) : (
                <>
                  <h1 className="text-[15px] font-semibold text-foreground leading-tight truncate">{layout.name}</h1>
                  <button
                    type="button"
                    onClick={startTitleEdit}
                    aria-label={t('common.rename', 'Rename')}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground leading-tight truncate">
              {t('playlist.addedOn', 'Added on')} {formatDate(layout.created_at, i18n.language)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {dirty && (
            <Button onClick={saveChanges} disabled={saving} className="gap-2 h-9">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.saveChanges', 'Save changes')}
            </Button>
          )}
          <div className="flex items-stretch h-9 rounded-md overflow-hidden border border-border bg-white text-foreground">
            <button
              type="button"
              onClick={() => setPublishMode('publish')}
              className="flex items-center gap-2 px-3 text-sm font-semibold hover:bg-muted transition-colors"
            >
              <Upload className="w-4 h-4" />
              {t('playlist.publish', 'Publish')}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={t('playlist.publishOptions', 'Publish options')}
                className="px-2 border-s border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center"
              >
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => setPublishMode('schedule')} className="gap-2">
                  <CalendarClock className="w-4 h-4" />
                  {t('playlist.schedule', 'Schedule')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={t('common.more', 'More')}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-border bg-muted/40 text-foreground hover:bg-muted transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => toast.info(t('playlist.duplicateSoon', 'Duplicate is coming soon.'))} className="gap-2">
                <Copy className="w-4 h-4" />
                {t('common.duplicate', 'Duplicate')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="gap-2 text-destructive focus:text-destructive">
                <Trash2 className="w-4 h-4" />
                {t('common.delete', 'Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, editingTitle, titleDraft, i18n.language, headerThumb, dirty, saving])

  usePageHeader(infoBar ? { render: infoBar } : { title: t('contentHub.tabs.layouts', 'Layouts') })

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (!layout) return null

  const templateDef = getTemplate(layout.template)
  const totalRows = activeZone ? rowsForZone(activeZone).length : 0

  // Schedule dialog target → resolve to the actual row label + current value.
  const scheduleZone = scheduleTarget ? layout.zones.find(z => z.id === scheduleTarget.split(':')[0]) : undefined
  const scheduleRowKey = scheduleTarget ? scheduleTarget.split(':')[1] : undefined
  const scheduleValue: RowSchedule = scheduleZone && scheduleRowKey
    ? getSchedule(scheduleZone, scheduleRowKey)
    : DEFAULT_ROW_SCHEDULE
  const scheduleRowLabel = (() => {
    if (!scheduleZone || !scheduleRowKey) return ''
    const rows = rowsForZone(scheduleZone)
    const r = rows.find(x => x.key === scheduleRowKey)
    return r ? (r.kind === 'media' ? r.item.original_name : r.item.name) : ''
  })()

  return (
    <div className="-m-4 sm:-m-[var(--space-l)] p-4 sm:p-[var(--space-l)] h-[calc(100dvh-3.5rem-1px)] flex flex-col bg-background">
      {/* Two distinct cards on the canvas — the content editor and the preview
          each read as their own bordered, rounded card with a gutter between
          them (mirrors the screen page's Content | Preview split). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">

        {/* ── Left column — active zone content editor ── */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          {/* Content header — the audio layer was removed, so the left column
              is content-only (no Content/Audio tab strip). */}
          <div className="inline-flex self-start items-center gap-1.5 mb-4 text-sm font-medium text-foreground">
            <LayoutIcon className="w-4 h-4" />
            {t('playlist.tabContent', 'Content')}
            {totalRows > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{totalRows}</Badge>
            )}
          </div>

          <>
              {/* Zone selector — a row of pills, one per zone in the layout. */}
              <div className="mb-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">{t('layout.zone', 'Zone')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {layout.zones.map((z, i) => (
                    <button
                      key={z.id}
                      type="button"
                      onClick={() => setActiveZoneId(z.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        activeZoneId === z.id
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-muted/40 text-foreground hover:border-primary/40'
                      }`}
                    >
                      <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] ${
                        activeZoneId === z.id ? 'bg-primary-foreground/20' : 'bg-muted-foreground/20'
                      }`}>{i + 1}</span>
                      {z.name}
                    </button>
                  ))}
                </div>
              </div>

              {isMobile && activeRows.length > 1 && (
                <p className="text-[11px] text-muted-foreground mb-2">
                  {t('layout.reorderNeedsDesktop', 'Reordering by drag works best on a larger screen.')}
                </p>
              )}

              <div className="flex-1 overflow-y-auto -mx-2 px-2">
                {activeRows.length === 0 ? (
                  <EmptyZoneState />
                ) : (
                  <ul className="space-y-2">
                    {activeRows.map((row, idx) => {
                      const sched   = activeZone ? getSchedule(activeZone, row.key) : DEFAULT_ROW_SCHEDULE
                      const visible = isVisibleAt(sched, now)
                      return (
                        <ContentRowView
                          key={row.key}
                          row={row}
                          index={idx}
                          isActive={false}
                          thumbCache={thumbCache}
                          dragKey={dragKey}
                          dragOverKey={dragOverKey}
                          schedule={sched}
                          visible={visible}
                          onDragStart={() => setDragKey(row.key)}
                          onDragOver={(e, dropBelow) => {
                            e.preventDefault()
                            setDragOverKey(row.key + (dropBelow ? ':below' : ':above'))
                          }}
                          onDragLeave={() => setDragOverKey(prev => prev?.startsWith(row.key) ? null : prev)}
                          onDrop={(dropBelow) => {
                            if (dragKey) handleDrop(dragKey, row.key, dropBelow)
                            setDragKey(null); setDragOverKey(null)
                          }}
                          onDragEnd={() => { setDragKey(null); setDragOverKey(null) }}
                          onRemove={() => handleRemoveRow(row.key)}
                          onDurationChange={(s) => handleDurationChange(row.key, s)}
                          onScheduleClick={() =>
                            activeZone && setScheduleTarget(`${activeZone.id}:${row.key}`)
                          }
                          onToggleMute={
                            row.kind === 'media' && row.isVideo
                              ? () => handleToggleMute(row.item.id)
                              : undefined
                          }
                        />
                      )
                    })}
                  </ul>
                )}
              </div>
            </>

          <div className="pt-4 mt-4 border-t border-border flex justify-center">
            <Button
              onClick={() => setPickerOpen(true)}
              // Force a real white button with dark text in BOTH themes — plain
              // `bg-white` can get re-themed in the dark app-shell.
              className="gap-2 h-11 px-6 bg-[#ffffff] text-zinc-900 hover:bg-zinc-100 border border-border"
            >
              <Plus className="w-4 h-4" />
              {t('playlist.addContent', 'Add Content')}
            </Button>
          </div>
        </div>

        {/* ── Right column — Zone-tiled preview ───────────────────── */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t('layout.zone', 'Zone')}</p>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground text-background text-[10px] font-semibold">
                  {Math.max(1, layout.zones.findIndex(z => z.id === activeZoneId) + 1)}
                </span>
                <span className="text-sm font-semibold">{activeZone?.name ?? '—'}</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setChangeLayoutOpen(true)}
              className="gap-2"
            >
              <LayoutIcon className="w-4 h-4" />
              {t('layout.changeLayout', 'Change Layout')}
            </Button>
          </div>

          <div className="flex-1 flex items-center justify-center min-h-0">
            <ZonePreviewFrame
              layoutZones={layout.zones}
              zoneRowsMap={zoneRowsMap}
              template={templateDef}
              activeZoneId={activeZoneId}
              expanded={zoneExpanded}
              previewing={previewing}
              thumbCache={thumbCache}
              columnWeights={draftColWeights ?? templateDef.defaultColWeights}
              rowWeights={draftRowWeights ?? templateDef.defaultRowWeights}
              onActivateZone={setActiveZoneId}
              onDraftWeights={(cols, rows) => {
                setDraftColWeights(cols)
                setDraftRowWeights(rows)
              }}
              onCommitWeights={(cols, rows) => persistWeights(cols, rows)}
            />
          </div>

          <div className="pt-4 mt-4 flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              disabled={!hasAnyContent}
              onClick={togglePreview}
              className="gap-2 h-10 px-4 bg-muted/40 text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              {previewing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {previewing ? t('playlist.stopPreview', 'Stop Preview') : t('playlist.playPreview', 'Play Preview')}
            </Button>
            <div className="flex items-center gap-2">
              {(draftColWeights || draftRowWeights) && (
                <button
                  type="button"
                  onClick={resetWeights}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
                >
                  {t('layout.resetSizesAction', 'Reset sizes')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setZoneExpanded(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                {zoneExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                {zoneExpanded ? t('layout.shrink', 'Shrink Zone') : t('layout.expand', 'Expand Zone')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Picker / dialogs ──────────────────────────────────────── */}
      <PlaylistContentPicker
        confirmLabel={t('layout.addToZone', 'Add to zone')}
        open={pickerOpen}
        excludeIds={activeZone?.media_ids ?? []}
        excludeLinkIds={activeZone?.link_ids ?? []}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleAppend}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={t('layout.deleteTitle', 'Delete Layout')}
        description={t('layout.deleteDesc', 'This layout will be removed. The media and links it references are not deleted.')}
        warning={
          layout.screens.length > 0
            ? t(
                'common.deleteLayoutDescPublished',
                'This layout is currently playing on {{count}} screen(s). Deleting it clears those screens — they go blank until you publish something else.',
                { count: layout.screens.length },
              )
            : undefined
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />

      <PublishDialog<LayoutData>
        open={publishMode !== null}
        mode={publishMode ?? 'publish'}
        kind="layout"
        // Flush buffered edits first so screens receive the just-edited layout,
        // then publish — the "publish commits the changes" path.
        publish={withCompanions(async payload => {
          await saveChanges()
          return publishSignageLayout(layout.id, payload)
        })}
        onClose={() => setPublishMode(null)}
        onPublished={updated => {
          const hydrated = { ...updated, zones: (updated.zones ?? []).map(hydrateZone) }
          setLayout(hydrated)
          setSavedSig(layoutSig(hydrated))
          setPublishMode(null)
        }}
      />

      <RowScheduleDialog
        open={scheduleTarget !== null}
        rowLabel={scheduleRowLabel}
        value={scheduleValue}
        onClose={() => setScheduleTarget(null)}
        onSave={(next) => {
          if (scheduleZone && scheduleRowKey) updateSchedule(scheduleZone.id, scheduleRowKey, next)
          setScheduleTarget(null)
        }}
      />

      {/* Change Layout — pick a different template arrangement. */}
      <ChangeLayoutDialog
        open={changeLayoutOpen}
        currentTemplate={layout.template}
        onClose={() => setChangeLayoutOpen(false)}
        onConfirm={handleChangeTemplate}
      />

      {/* Unsaved-changes guard — fires when leaving with buffered edits. */}
      <ConfirmDialog
        open={guardOpen}
        title={t('common.unsavedTitle', 'Leave without publishing?')}
        description={t('common.unsavedDesc', "You didn't publish your changes. Your changes will be lost if you leave.")}
        confirmLabel={t('common.leaveDiscard', 'Leave & discard')}
        onConfirm={confirmLeave}
        onClose={cancelLeave}
      />
    </div>
  )
}

// ── ZonePreviewFrame ─────────────────────────────────────────────────────
function ZonePreviewFrame({
  layoutZones, zoneRowsMap, template, activeZoneId, expanded, previewing,
  thumbCache, columnWeights, rowWeights,
  onActivateZone, onDraftWeights, onCommitWeights,
}: {
  layoutZones: LayoutZone[]
  zoneRowsMap: Record<string, ContentRow[]>
  template: TemplateDef
  activeZoneId: string
  expanded: boolean
  previewing: boolean
  thumbCache: Record<number, string>
  columnWeights: number[]
  rowWeights: number[]
  onActivateZone: (id: string) => void
  onDraftWeights: (cols: number[], rows: number[]) => void
  onCommitWeights: (cols: number[], rows: number[]) => void
}) {
  const frameRef = useRef<HTMLDivElement | null>(null)
  // Zone boundaries must be visible here (this is a zone diagram) — the default
  // `border`/`bg-border` tokens track the surface (borderless by design), so use
  // a subtle-but-visible tone for the frame + inter-zone gaps in both themes.
  const frame = 'relative w-full max-w-xl aspect-video rounded-xl border-2 border-muted-foreground/25 bg-muted/30 overflow-hidden'

  if (expanded) {
    const zone = layoutZones.find(z => z.id === activeZoneId)
    const rows = zone ? (zoneRowsMap[zone.id] ?? []) : []
    return (
      <div className={frame} dir="ltr">
        <ZonePreviewTile
          zone={zone}
          rows={rows}
          previewing={previewing}
          thumbCache={thumbCache}
        />
      </div>
    )
  }

  const startResize = (axis: 'col' | 'row', index: number) => (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const frameEl = frameRef.current
    if (!frameEl) return
    const rect = frameEl.getBoundingClientRect()
    const startCols = [...columnWeights]
    const startRows = [...rowWeights]
    const tracks = axis === 'col' ? startCols : startRows
    const totalPx = axis === 'col' ? rect.width : rect.height
    const totalWeight = tracks.reduce((s, w) => s + w, 0)
    const startA = tracks[index]
    const startB = tracks[index + 1]
    const startClient = axis === 'col' ? e.clientX : e.clientY
    const MIN = 0.1

    let latestCols = startCols
    let latestRows = startRows

    const onMove = (ev: PointerEvent) => {
      const cur = axis === 'col' ? ev.clientX : ev.clientY
      const deltaPx = cur - startClient
      const deltaWeight = (deltaPx / totalPx) * totalWeight
      let a = startA + deltaWeight
      let b = startB - deltaWeight
      if (a < MIN) { b -= (MIN - a); a = MIN }
      if (b < MIN) { a -= (MIN - b); b = MIN }
      if (axis === 'col') {
        latestCols = [...startCols]
        latestCols[index] = a
        latestCols[index + 1] = b
        onDraftWeights(latestCols, startRows)
      } else {
        latestRows = [...startRows]
        latestRows[index] = a
        latestRows[index + 1] = b
        onDraftWeights(startCols, latestRows)
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      onCommitWeights(latestCols, latestRows)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  const colTotal = columnWeights.reduce((s, w) => s + w, 0)
  const rowTotal = rowWeights.reduce((s, w) => s + w, 0)
  const colOffsetsPct = columnWeights.reduce<number[]>((acc, w, i) => {
    const prev = i === 0 ? 0 : acc[i - 1]
    acc.push(prev + (w / colTotal) * 100)
    return acc
  }, [])
  const rowOffsetsPct = rowWeights.reduce<number[]>((acc, w, i) => {
    const prev = i === 0 ? 0 : acc[i - 1]
    acc.push(prev + (w / rowTotal) * 100)
    return acc
  }, [])

  return (
    // Pinned LTR: this is a diagram of the physical zone layout the TV will
    // actually render — CSS Grid mirrors its column order under dir="rtl"
    // (inherited from the Arabic page), which would show zone 1 on the
    // right here while the webplayer (no dir attribute, always literal
    // left-to-right) always renders it on the left. Same fix as the
    // add-screen wizard's browser-window mock.
    <div className={frame} ref={frameRef} dir="ltr">
      <div
        className="absolute inset-0 bg-muted-foreground/25"
        style={{
          display: 'grid',
          ...gridTracks(template, columnWeights, rowWeights),
          gap: '2px',
        }}
      >
        {template.zones.map((slot, i) => {
          const zone = layoutZones[i]
          const rows = zone ? (zoneRowsMap[zone.id] ?? []) : []
          const isActive = zone?.id === activeZoneId
          return (
            <div
              key={zone?.id ?? `slot-${i}`}
              style={zoneStyle(slot)}
              className="relative bg-muted/30 overflow-hidden"
            >
              <ZonePreviewTile
                zone={zone}
                rows={rows}
                previewing={previewing}
                thumbCache={thumbCache}
              />
              <button
                type="button"
                onClick={() => zone && onActivateZone(zone.id)}
                className={`absolute inset-0 ${isActive ? 'ring-2 ring-primary ring-inset' : 'hover:bg-foreground/5'}`}
                aria-label={zone?.name ?? `Zone ${i + 1}`}
              />
              <span className="absolute top-1.5 start-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/60 text-white text-[10px] font-semibold pointer-events-none">
                {i + 1}
              </span>
            </div>
          )
        })}
      </div>

      {colOffsetsPct.slice(0, -1).map((pct, i) => (
        <div
          key={`col-${i}`}
          onPointerDown={startResize('col', i)}
          className="absolute top-0 bottom-0 w-2 -translate-x-1/2 cursor-col-resize z-10 group/handle"
          style={{ left: `${pct}%` }}
          aria-label={`Resize columns ${i + 1} and ${i + 2}`}
          role="separator"
        >
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-transparent group-hover/handle:bg-primary transition-colors" />
        </div>
      ))}

      {rowOffsetsPct.slice(0, -1).map((pct, i) => (
        <div
          key={`row-${i}`}
          onPointerDown={startResize('row', i)}
          className="absolute left-0 right-0 h-2 -translate-y-1/2 cursor-row-resize z-10 group/handle"
          style={{ top: `${pct}%` }}
          aria-label={`Resize rows ${i + 1} and ${i + 2}`}
          role="separator"
        >
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-0.5 bg-transparent group-hover/handle:bg-primary transition-colors" />
        </div>
      ))}
    </div>
  )
}

function ChangeLayoutDialog({
  open, currentTemplate, onClose, onConfirm,
}: {
  open: boolean
  currentTemplate: LayoutTemplate
  onClose: () => void
  onConfirm: (next: LayoutTemplate) => void
}) {
  const { t } = useTranslation()
  const [pending, setPending] = useState<LayoutTemplate>(currentTemplate)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setPending(currentTemplate)
  }, [open, currentTemplate])

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-5xl flex flex-col max-h-[92vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle>{t('layout.changeLayoutTitle', 'Choose Layout')}</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          <p className="text-xs text-muted-foreground mb-4">
            {t('layout.changeLayoutDesc', 'Switching templates keeps existing zone content for matching positions and adds empty zones for any new positions.')}
          </p>
          <LayoutPickerGrid value={pending} onChange={setPending} />
        </div>
        <DialogFooter className="gap-2 px-6 py-3 border-t border-border">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            disabled={pending === currentTemplate}
            onClick={() => onConfirm(pending)}
            className="gap-2"
          >
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── ZonePreviewTile ──────────────────────────────────────────────────────
function ZonePreviewTile({
  zone, rows, previewing, thumbCache,
}: {
  zone: LayoutZone | undefined
  rows: ContentRow[]
  previewing: boolean
  thumbCache: Record<number, string>
}) {
  const { t } = useTranslation()
  const [idx, setIdx] = useState(0)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    setIdx(0)
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [previewing, rows.length])

  useEffect(() => {
    if (!previewing) return
    const row = rows[idx]
    if (!row) return
    if (row.kind === 'media' && row.isVideo) return
    const ms = Math.max(1, defaultDurationFor(row)) * 1000
    timerRef.current = window.setTimeout(() => {
      setIdx(i => (i + 1 < rows.length ? i + 1 : i))
    }, ms)
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [previewing, idx, rows])

  const handleVideoEnded = () => {
    setIdx(i => (i + 1 < rows.length ? i + 1 : i))
  }

  if (rows.length === 0) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-1 pointer-events-none">
        <p className="text-[11px] font-medium">{t('layout.noContent', 'No content')}</p>
        {zone && <p className="text-[10px] opacity-70">{zone.name}</p>}
      </div>
    )
  }
  const row = rows[idx] ?? rows[0]
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <PreviewBody row={row} previewing={previewing} thumbCache={thumbCache} onVideoEnded={handleVideoEnded} />
    </div>
  )
}

function PreviewBody({
  row, previewing, thumbCache, onVideoEnded,
}: {
  row: ContentRow
  previewing: boolean
  thumbCache: Record<number, string>
  onVideoEnded: () => void
}) {
  if (row.kind === 'link') {
    return (
      <div className="flex flex-col items-center gap-1 text-center px-4">
        <Link2 className="w-8 h-8 text-teal-600" />
        <p className="text-[11px] font-medium text-foreground truncate max-w-[10rem]">{row.item.name}</p>
      </div>
    )
  }
  const m = row.item
  const url = m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`
  const thumb = thumbCache[m.id]
  if (row.isImage) return <img src={url} alt={m.original_name} className="w-full h-full object-contain" />
  if (row.isVideo) {
    if (previewing) {
      return <video src={url} autoPlay muted={row.muted} playsInline onEnded={onVideoEnded} className="w-full h-full object-contain" />
    }
    return thumb
      ? <img src={thumb} alt={m.original_name} className="w-full h-full object-contain" />
      : <video src={`${url}#t=0.1`} className="w-full h-full object-contain" muted playsInline preload="metadata" />
  }
  if (row.isPdf) return <PdfThumbnail url={url} targetWidth={320} fit="contain" className="w-full h-full" />
  return <FileType className="w-10 h-10 text-muted-foreground" />
}

// ── ContentRowView ───────────────────────────────────────────────────────
// Mirrors PlaylistDetailsPage's row — adds the per-row mute (videos) +
// per-row schedule controls so layouts get the same row toolbox as playlists.
function ContentRowView({
  row, index, isActive, thumbCache,
  dragKey, dragOverKey,
  schedule, visible,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
  onRemove, onDurationChange, onScheduleClick, onToggleMute,
}: {
  row: ContentRow
  index: number
  isActive: boolean
  thumbCache: Record<number, string>
  dragKey: string | null
  dragOverKey: string | null
  schedule: RowSchedule
  visible: boolean
  onDragStart: () => void
  onDragOver: (e: React.DragEvent, dropBelow: boolean) => void
  onDragLeave: () => void
  onDrop: (dropBelow: boolean) => void
  onDragEnd: () => void
  onRemove: () => void
  onDurationChange: (seconds: number) => void
  onScheduleClick: () => void
  onToggleMute?: () => void
}) {
  const { t } = useTranslation()
  const isVideo = row.kind === 'media' && row.isVideo
  const isImage = row.kind === 'media' && row.isImage
  const isPdf   = row.kind === 'media' && row.isPdf
  const isLink  = row.kind === 'link'

  const url = row.kind === 'media'
    ? (row.item.url.startsWith('http') ? row.item.url : `${BACKEND_URL}${row.item.url}`)
    : ''
  const thumb = row.kind === 'media' ? thumbCache[row.item.id] : undefined

  const isOverAbove = dragOverKey === `${row.key}:above` && dragKey && dragKey !== row.key
  const isOverBelow = dragOverKey === `${row.key}:below` && dragKey && dragKey !== row.key

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={e => {
        const rect = (e.currentTarget as HTMLLIElement).getBoundingClientRect()
        const dropBelow = e.clientY > rect.top + rect.height / 2
        onDragOver(e, dropBelow)
      }}
      onDragLeave={onDragLeave}
      onDrop={e => {
        e.preventDefault()
        const rect = (e.currentTarget as HTMLLIElement).getBoundingClientRect()
        const dropBelow = e.clientY > rect.top + rect.height / 2
        onDrop(dropBelow)
      }}
      onDragEnd={onDragEnd}
      className={`group relative flex items-center gap-3 p-3 rounded-xl border bg-muted/40 transition-colors cursor-grab active:cursor-grabbing ${
        isActive ? 'border-primary ring-2 ring-primary/30' : 'border-border/60 hover:bg-muted/60'
      } ${dragKey === row.key ? 'opacity-50' : ''} ${!visible ? 'opacity-60' : ''}`}
    >
      {isOverAbove && <span className="absolute -top-1 inset-x-0 h-0.5 bg-primary rounded-full" />}
      {isOverBelow && <span className="absolute -bottom-1 inset-x-0 h-0.5 bg-primary rounded-full" />}

      <GripVertical className="w-4 h-4 text-muted-foreground/60 shrink-0" />
      <span className="text-xs text-muted-foreground font-mono w-5 text-center shrink-0">{index + 1}</span>

      <div className="w-16 h-10 rounded-lg overflow-hidden bg-muted shrink-0 border border-border/40">
        {isImage && <img src={url} alt={row.kind === 'media' ? row.item.original_name : ''} className="w-full h-full object-cover" loading="lazy" />}
        {isVideo && thumb && <img src={thumb} alt="" className="w-full h-full object-cover" />}
        {isVideo && !thumb && (
          <div className="w-full h-full flex items-center justify-center bg-purple-500/10">
            <Video className="w-4 h-4 text-purple-400" />
          </div>
        )}
        {isPdf && (
          <div className="w-full h-full flex items-center justify-center bg-orange-500/10">
            <FileText className="w-4 h-4 text-orange-500" />
          </div>
        )}
        {isLink && (
          <div className="w-full h-full flex items-center justify-center bg-teal-500/10">
            <Link2 className="w-4 h-4 text-teal-600" />
          </div>
        )}
        {row.kind === 'media' && !isImage && !isVideo && !isPdf && (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <FileType className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {row.kind === 'media' ? row.item.original_name : row.item.name}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {isImage && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
              <ImageIcon className="w-2 h-2" /> {t('common.image', 'Image')}
            </Badge>
          )}
          {isVideo && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
              <Video className="w-2 h-2" /> {t('common.video', 'Video')}
            </Badge>
          )}
          {isPdf && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-0">
              <FileText className="w-2 h-2" /> {t('common.document', 'Document')}
            </Badge>
          )}
          {isLink && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border-0">
              <Link2 className="w-2 h-2" /> {t('common.link', 'Webpage')}
            </Badge>
          )}
          {!schedule.alwaysOn && (
            <Badge
              variant="secondary"
              className={`text-[9px] gap-0.5 py-0 h-4 border-0 ${
                visible
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <CalendarClock className="w-2 h-2" />
              {visible
                ? t('playlist.rowScheduled', 'Scheduled')
                : t('playlist.rowHidden', 'Hidden')}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
        <Clock className="w-3 h-3" />
        {isVideo ? (
          <span className="font-medium">
            {fmtDur(defaultDurationFor(row))} <span className="text-[10px] opacity-70">({t('playlist.auto', 'Auto')})</span>
          </span>
        ) : (
          <DurationInput
            value={defaultDurationFor(row)}
            onCommit={onDurationChange}
            ariaLabel={t('playlist.rowDurationAria', 'Duration in seconds')}
          />
        )}
      </div>

      {onToggleMute && row.kind === 'media' && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onToggleMute() }}
          onMouseDown={e => e.stopPropagation()}
          draggable={false}
          aria-label={row.muted
            ? t('playlist.rowUnmute', 'Unmute video')
            : t('playlist.rowMute', 'Mute video')}
          title={row.muted
            ? t('playlist.rowUnmute', 'Unmute video')
            : t('playlist.rowMute', 'Mute video')}
          className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors shrink-0 ${
            row.muted
              ? 'text-amber-600 opacity-100 hover:bg-amber-500/10'
              : 'text-muted-foreground opacity-0 group-hover:opacity-100 touch-reveal hover:text-foreground hover:bg-muted'
          }`}
        >
          {row.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      )}

      <button
        type="button"
        onClick={e => { e.stopPropagation(); onScheduleClick() }}
        onMouseDown={e => e.stopPropagation()}
        draggable={false}
        aria-label={t('playlist.rowScheduleAria', 'Schedule this item')}
        className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors shrink-0 ${
          !schedule.alwaysOn
            ? 'text-primary opacity-100'
            : 'text-muted-foreground opacity-0 group-hover:opacity-100 touch-reveal hover:text-foreground hover:bg-muted'
        }`}
      >
        <CalendarClock className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('common.remove', 'Remove')}
        className="w-7 h-7 max-md:w-10 max-md:h-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0 opacity-0 group-hover:opacity-100 touch-reveal"
      >
        <X className="w-4 h-4" />
      </button>
    </li>
  )
}

function EmptyZoneState() {
  const { t } = useTranslation()
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12 text-muted-foreground">
      <LayoutIcon className="w-10 h-10 mb-3 opacity-50" />
      <h3 className="text-base font-semibold text-foreground mb-1">
        {t('layout.zoneEmptyTitle', 'This zone has no content yet')}
      </h3>
      <p className="text-sm max-w-sm leading-relaxed">
        {t('layout.zoneEmptyDescription', 'Add media, documents, or links to fill this zone.')}
      </p>
    </div>
  )
}

